import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from './useAuth'
import { resolveCustomInputLabels } from '@/lib/utils'
import type { Order, OrderItem, Product, FulfillmentStatus, CustomerProfile } from '@/types/database'

export type OrderWithProduct = Order & {
  product: Pick<Product, 'title' | 'slug' | 'video_thumbnail_url' | 'hero_image_url'> | null
}

export function useOrders(statusFilter?: FulfillmentStatus | 'all') {
  const { user } = useAuth()
  const [orders, setOrders] = useState<OrderWithProduct[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchOrders = useCallback(async () => {
    if (!user) {
      setOrders([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)

    let query = supabase
      .from('orders')
      .select('*, product:products(title, slug, video_thumbnail_url, hero_image_url)')
      .or(`user_id.eq.${user.id},customer_email.eq.${user.email}`)
      .order('created_at', { ascending: false })

    if (statusFilter && statusFilter !== 'all') {
      query = query.eq('fulfillment_status', statusFilter)
    }

    const { data, error: err } = await query

    if (err) {
      setError(err.message)
    } else {
      setOrders((data as OrderWithProduct[]) || [])
    }
    setIsLoading(false)
  }, [user, statusFilter])

  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  // Real-time subscription for customer orders
  useEffect(() => {
    if (!user) return

    const channel = supabase
      .channel('customer-orders')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          setOrders(prev =>
            prev.map(o => o.id === payload.new.id ? { ...o, ...payload.new } as OrderWithProduct : o)
          )
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Refetch to get full product join
          fetchOrders()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, fetchOrders])

  return { orders, isLoading, error, refetch: fetchOrders }
}

export function useOrder(orderId: string | undefined) {
  const { user } = useAuth()
  const [order, setOrder] = useState<OrderWithProduct | null>(null)
  const [orderItems, setOrderItems] = useState<OrderItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user || !orderId) {
      setIsLoading(false)
      return
    }

    const currentUser = user

    async function fetchOrder() {
      setIsLoading(true)

      const [orderResult, itemsResult] = await Promise.all([
        supabase
          .from('orders')
          .select('*, product:products(title, slug, video_thumbnail_url, hero_image_url)')
          .eq('id', orderId!)
          .or(`user_id.eq.${currentUser.id},customer_email.eq.${currentUser.email}`)
          .maybeSingle(),
        supabase
          .from('order_items')
          .select('*')
          .eq('order_id', orderId!)
          .order('created_at', { ascending: true }),
      ])

      if (orderResult.error) {
        setError(orderResult.error.message)
      } else {
        setOrder(orderResult.data as unknown as OrderWithProduct)
      }

      // Resolve UUID custom_input keys to human-readable labels
      let items = (itemsResult.data as OrderItem[]) || []
      if (items.length > 0) {
        const productIds = [...new Set(items.map(i => i.product_id).filter((id): id is string => !!id))]
        if (productIds.length > 0) {
          const { data: products } = await supabase
            .from('products')
            .select('id, custom_inputs')
            .in('id', productIds)

          if (products) {
            const defsMap = new Map(products.map(p => [p.id, p.custom_inputs]))
            items = items.map(item => ({
              ...item,
              custom_inputs: resolveCustomInputLabels(
                item.custom_inputs as Record<string, string> | null,
                item.product_id ? defsMap.get(item.product_id) : undefined
              ),
            }))
          }
        }
      }

      setOrderItems(items)
      setIsLoading(false)
    }

    fetchOrder()
  }, [user, orderId])

  // Real-time subscription for this specific order
  useEffect(() => {
    if (!orderId) return

    const channel = supabase
      .channel(`order-${orderId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `id=eq.${orderId}`,
        },
        (payload) => {
          setOrder(prev => prev ? { ...prev, ...payload.new } as OrderWithProduct : prev)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [orderId])

  return { order, orderItems, isLoading, error }
}

export function useRecentOrders(limit = 3) {
  const { user } = useAuth()
  const [orders, setOrders] = useState<OrderWithProduct[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!user) {
      setOrders([])
      setIsLoading(false)
      return
    }

    const currentUser = user

    async function fetchOrders() {
      setIsLoading(true)

      const [ordersResult, countResult] = await Promise.all([
        supabase
          .from('orders')
          .select('*, product:products(title, slug, video_thumbnail_url, hero_image_url)')
          .or(`user_id.eq.${currentUser.id},customer_email.eq.${currentUser.email}`)
          .order('created_at', { ascending: false })
          .limit(limit),
        supabase
          .from('orders')
          .select('*', { count: 'exact', head: true })
          .or(`user_id.eq.${currentUser.id},customer_email.eq.${currentUser.email}`),
      ])

      setOrders((ordersResult.data as unknown as OrderWithProduct[]) || [])
      setTotalCount(countResult.count || 0)
      setIsLoading(false)
    }

    fetchOrders()
  }, [user, limit])

  return { orders, totalCount, isLoading }
}

export function useCustomerProfile() {
  const { user } = useAuth()
  const [profile, setProfile] = useState<CustomerProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchProfile = useCallback(async () => {
    if (!user) {
      setProfile(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    const { data } = await supabase
      .from('customer_profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle()

    setProfile(data as CustomerProfile | null)
    setIsLoading(false)
  }, [user])

  useEffect(() => {
    fetchProfile()
  }, [fetchProfile])

  const updateProfile = async (updates: Partial<CustomerProfile>) => {
    if (!user) return { error: 'Not authenticated' }

    const { error } = await supabase
      .from('customer_profiles')
      .update(updates)
      .eq('id', user.id)

    if (!error) {
      await fetchProfile()
    }

    return { error: error?.message || null }
  }

  return { profile, isLoading, updateProfile, refetch: fetchProfile }
}
